import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Button from "./Button";
import ListWrapper from "../utils/ListWrapper";
import D3EAppTemplate from "../models/D3EAppTemplate";
import Popup from "./Popup";
import MaterialIcons from "../icons/MaterialIcons";
import TextView from "./TextView";
import IconButton from "./IconButton";
import Divider from "./Divider";
import TemplateOverView from "./TemplateOverView";
import { BuildContext } from "../classes/BuildContext";

type _TemplateCardViewOnAddBuildTemplate = (template: D3EAppTemplate) => void;

type _TemplateCardViewOnRemoveBuildTemplate = (
  template: D3EAppTemplate
) => void;

type _ColumnRefOnTap = (d3eState: TemplateCardViewRefs) => void;

type _AddRefOnPressed = (d3eState: TemplateCardViewRefs) => void;

type _AddedRefOnPressed = (d3eState: TemplateCardViewRefs) => void;

type _ButtonOnPressed = (d3eState: TemplateCardViewRefs) => void;

export interface TemplateCardViewProps extends BaseUIProps {
  key?: string;
  obj: D3EAppTemplate;
  appTemplateList: Array<D3EAppTemplate>;
  _appTemplateListHash?: number;
  onAddBuildTemplate?: _TemplateCardViewOnAddBuildTemplate;
  onRemoveBuildTemplate?: _TemplateCardViewOnRemoveBuildTemplate;
}
/// To store state data for TemplateCardView
class TemplateCardViewRefs {
  public button: ButtonState = new ButtonState();
  public columnRef: ColumnRefState = new ColumnRefState();
}

interface ButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: TemplateCardViewRefs;
  _viewDetailsButtonHandler?: _ButtonOnPressed;
}

class ButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _ButtonWithState extends ObservableComponent<ButtonWithStateProps> {
  buttonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get button(): ButtonState {
    return this.props.d3eState.button;
  }
  public get d3eState(): TemplateCardViewRefs {
    return this.props.d3eState;
  }
  public get _viewDetailsButtonHandler(): _ButtonOnPressed {
    return this.props._viewDetailsButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("button", null, this.button);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["button"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      expand: true,
      margin: ui.EdgeInsets.fromLTRB(0.0, 10.0, 0.0, 0.0, new Map()),
      child: Button({
        padding: ui.EdgeInsets.all(10.0, new Map()),
        decoration: cStyle.tButtonOutlineButtonDecorationOn,
        disable: this.button.disable,
        onPressed: () => {
          this._viewDetailsButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({
          data: "View Details",
          style: new ui.TextStyle({
            color: new ui.Color(0xff3c3e49),
            fontSize: 12,
            fontWeight: ui.FontWeight.w600,
          }),
          className: "x153 hc",
        }),
      }),
      className: "x64 hc h",
    });
  }
}
function ButtonWithState(props: ButtonWithStateProps) {
  return React.createElement(_ButtonWithState, props);
}

interface ColumnRefWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: TemplateCardViewRefs;
  _onAddRefHandler?: _AddRefOnPressed;
  _onAddedRefHandler?: _AddedRefOnPressed;
  _onCardTapHandler?: _ColumnRefOnTap;
  _viewDetailsButtonHandler?: _ButtonOnPressed;
  appTemplateList: Array<D3EAppTemplate>;
  obj: D3EAppTemplate;
}

class ColumnRefState extends ObjectObservable {
  private _hover: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _ColumnRefWithState extends ObservableComponent<ColumnRefWithStateProps> {
  buttonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ColumnRefWithStateProps) {
    super(props);

    this.initState();
  }
  public get appTemplateList(): Array<D3EAppTemplate> {
    return this.props.appTemplateList;
  }
  public get button(): ButtonState {
    return this.props.d3eState.button;
  }
  public get columnRef(): ColumnRefState {
    return this.props.d3eState.columnRef;
  }
  public get obj(): D3EAppTemplate {
    return this.props.obj;
  }
  public get d3eState(): TemplateCardViewRefs {
    return this.props.d3eState;
  }
  public get _onAddRefHandler(): _AddRefOnPressed {
    return this.props._onAddRefHandler;
  }
  public get _onAddedRefHandler(): _AddedRefOnPressed {
    return this.props._onAddedRefHandler;
  }
  public get _onCardTapHandler(): _ColumnRefOnTap {
    return this.props._onCardTapHandler;
  }
  public get _viewDetailsButtonHandler(): _ButtonOnPressed {
    return this.props._viewDetailsButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("button", null, this.button);

    this.updateObservable("columnRef", null, this.columnRef);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.on(
      [
        "appTemplateList",
        "button",
        "columnRef",
        "columnRef.hover",
        "obj",
        "obj.backgroud",
        "obj.icon",
        "obj.title",
        "obj.topFeatures",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: ColumnRefWithStateProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }
  }
  public columnRefOnEnter(event): void {
    return this.columnRef.setHover(true);
  }
  public columnRefOnExit(event): void {
    return this.columnRef.setHover(false);
  }
  public dispose(): void {
    this.columnRef.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      states: ui.joinStates(
        { "data-c0": this.columnRef.hover },
        this.props.states
      ),
      children: [
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.Wrap({
              crossAxisAlignment: ui.WrapCrossAlignment.center,
              spacing: 5,
              children: [
                ui.NetworkImage({
                  url: this.obj.icon?.downloadUrl ?? "",
                  height: 40,
                  width: 50,
                  className: "xb82 hc vc",
                  key: "0",
                }),
                ui.Column({
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    TextView({
                      data: "BUILD AN APP LIKE",
                      textAlign: ui.TextAlign.start,
                      style: new ui.TextStyle({
                        fontSize: 10,
                        color: new ui.Color(0xff83889e),
                      }),
                      className: "x4ba8",
                      key: "0",
                    }),
                    TextView({
                      data: this.obj.title,
                      style: new ui.TextStyle({
                        fontSize: 20,
                        color: new ui.Color(0xff3c3e49),
                        fontWeight: ui.FontWeight.bold,
                      }),
                      className: "x715",
                      key: "1",
                    }),
                  ],
                  className: "x798 vc v",
                  key: "1",
                }),
              ],
              className: "x09 vc v",
              key: "0",
            }),
            !this.appTemplateList.contains(this.obj)
              ? ui.Container({
                  decoration: new ui.BoxDecoration({
                    color: new ui.Color(0xfff0e5ff),
                    borderRadius: ui.BorderRadius.circular(3.0),
                  }),
                  margin: ui.EdgeInsets.all(5.0, new Map()),
                  padding: ui.EdgeInsets.all(5.0, new Map()),
                  child: IconButton({
                    icon: MaterialIcons.add,
                    color: new ui.Color(0xff6c00ea),
                    size: 20,
                    onPressed: () => {
                      this._onAddRefHandler(this.d3eState);
                    },
                  }),
                  className: "xba vc",
                })
              : ui.Container({
                  decoration: new ui.BoxDecoration({
                    color: new ui.Color(0xffcdf6e4),
                    borderRadius: ui.BorderRadius.circular(3.0),
                  }),
                  margin: ui.EdgeInsets.all(5.0, new Map()),
                  padding: ui.EdgeInsets.all(5.0, new Map()),
                  child: IconButton({
                    icon: MaterialIcons.check,
                    color: new ui.Color(0xff4caf50),
                    size: 25,
                    onPressed: () => {
                      this._onAddedRefHandler(this.d3eState);
                    },
                  }),
                  className: "xb85 vc",
                }),
          ],
          className: "xa4c hc vc h",
          key: "0",
        }),
        Divider({ className: "xeb6 hc h", key: "1" }),
        ui.Column({
          children: [
            ui.NetworkImage({
              url: this.obj.backgroud?.downloadUrl ?? "",
              height: 200,
              fit: ui.BoxFit.cover,
              className: "x3ff hc vc",
              key: "0",
            }),
          ],
          className: "x4cc hc vc",
          key: "2",
        }),
        Divider({ className: "x415 hc h", key: "3" }),
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            TextView({
              data: "TOP FEATURES",
              textAlign: ui.TextAlign.start,
              style: new ui.TextStyle({
                fontSize: 10,
                color: new ui.Color(0xff83889e),
              }),
              className: "xd3c hc",
              key: "0",
            }),
            TextView({
              data: this.obj.topFeatures,
              style: new ui.TextStyle({ color: new ui.Color(0xff3c3e49) }),
              className: "x3d hc",
              key: "1",
            }),
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.center,
              children: [
                ButtonWithState({
                  d3eState: this.d3eState,
                  _viewDetailsButtonHandler: this._viewDetailsButtonHandler,
                  key: "0",
                }),
              ],
              className: "x4f hc h",
              key: "2",
            }),
          ],
          className: "x6c8 hc h",
          key: "4",
        }),
      ],
      onEnter: (event) => {
        this.columnRefOnEnter(event);
      },
      onExit: (event) => {
        this.columnRefOnExit(event);
      },
      onTap: (e) => {
        e.stopPropagation();

        this._onCardTapHandler(this.d3eState);
      },
      className: ui.join(this.props.className, "TemplateCardView xa45 hc vc"),
      ...copyBaseUIProps(this.props),
    });
  }
}
function ColumnRefWithState(props: ColumnRefWithStateProps) {
  return React.createElement(_ColumnRefWithState, props);
}

class _TemplateCardViewState extends ObservableComponent<TemplateCardViewProps> {
  static defaultProps = {
    obj: null,
    appTemplateList: [],
    onAddBuildTemplate: null,
    onRemoveBuildTemplate: null,
  };
  d3eState: TemplateCardViewRefs = new TemplateCardViewRefs();
  isReadyToBuild: boolean = false;
  viewTemplateDetailsPopup: Popup;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: TemplateCardViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): D3EAppTemplate {
    return this.props.obj;
  }
  public get appTemplateList(): Array<D3EAppTemplate> {
    return this.props.appTemplateList;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.subscribeToList(this.appTemplateList, "appTemplateList");

    this.updateSyncCollProperty("appTemplateList", this.props.appTemplateList);

    this.on(["appTemplateList", "obj"], this.rebuild);
  }
  public componentDidUpdate(prevProps: TemplateCardViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }

    if (prevProps.appTemplateList !== this.props.appTemplateList) {
      this.updateObservableColl(
        "appTemplateList",
        prevProps.appTemplateList,
        this.props.appTemplateList
      );

      this.fire("appTemplateList", this);
    }
  }
  public setIsReadyToBuild(val: boolean): void {
    let isValChanged: boolean = this.isReadyToBuild !== val;

    if (!isValChanged) {
      return;
    }

    this.isReadyToBuild = val;

    this.fire("isReadyToBuild", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ColumnRefWithState({
      d3eState: this.d3eState,
      _onAddRefHandler: this.onAddRefHandler,
      _onAddedRefHandler: this.onAddedRefHandler,
      _onCardTapHandler: this.onCardTapHandler,
      _viewDetailsButtonHandler: this.viewDetailsButtonHandler,
      appTemplateList: this.appTemplateList,
      obj: this.obj,
      className: this.props.className,
      ...copyBaseUIProps(this.props),
    });
  }
  public onAddRefHandler = (d3eState: TemplateCardViewRefs): void => {
    this.setIsReadyToBuild(true);

    this.onAddBuildTemplate(this.obj);
  };
  public onAddedRefHandler = (d3eState: TemplateCardViewRefs): void => {
    this.setIsReadyToBuild(false);

    this.onRemoveBuildTemplate(this.obj);
  };
  public onCardTapHandler = (d3eState: TemplateCardViewRefs): void => {
    this.setIsReadyToBuild(!this.isReadyToBuild);

    if (this.isReadyToBuild) {
      this.onAddBuildTemplate(this.obj);
    } else {
      this.onRemoveBuildTemplate(this.obj);
    }
  };
  public viewDetailsButtonHandler = (d3eState: TemplateCardViewRefs): void => {
    this.showViewTemplateDetails({ autoClose: true });
  };
  public viewTemplateDetailsHandler = (obj: D3EAppTemplate): void => {
    this.setIsReadyToBuild(true);

    this.onAddBuildTemplate(obj);

    this.hideViewTemplateDetails();
  };
  public get onAddBuildTemplate(): _TemplateCardViewOnAddBuildTemplate {
    return this.props.onAddBuildTemplate;
  }
  public get onRemoveBuildTemplate(): _TemplateCardViewOnRemoveBuildTemplate {
    return this.props.onRemoveBuildTemplate;
  }
  public dispose(): void {
    this.viewTemplateDetailsPopup?.dispose();

    super.dispose();
  }
  public showViewTemplateDetails(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.viewTemplateDetailsPopup?.dispose();

    this.viewTemplateDetailsPopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Center,
      child: ui.Container({
        width: 1000,
        child: TemplateOverView({
          obj: this.obj,
          onAddTemplate: (obj) => {
            this.viewTemplateDetailsHandler(obj);
          },
        }),
        className: "x9c8 hc vc",
      }),
    });

    this.viewTemplateDetailsPopup.showPopup(this.context);
  }
  public hideViewTemplateDetails(): void {
    this.viewTemplateDetailsPopup?.dispose();
  }
  public get button() {
    return this.d3eState.button;
  }
  public get columnRef() {
    return this.d3eState.columnRef;
  }
}
export default function TemplateCardView(props: TemplateCardViewProps) {
  return React.createElement(
    _TemplateCardViewState,
    { ..._TemplateCardViewState.defaultProps, ...props },
    ListWrapper.fromInput<D3EAppTemplate>(
      props.appTemplateList,
      "appTemplateList"
    )
  );
}
